import React, { useState } from 'react';
import Fade from 'react-reveal/Fade';
import { useSite } from '../context/SiteContext';
import Skeleton from 'react-loading-skeleton';

/* ─────────────────────────────────────────
   FAQ ITEM
───────────────────────────────────────── */
const FAQItem = ({ item, isOpen, onClick }) => {
  const renderAnswerContent = () => {
    if (item.isStructured) {
      return (
        <div className="faq-structured-answer">
          <p className="faq-intro">{item.intro}</p>
          <div className="faq-options-grid">
            {item.options.map((opt, i) => (
              <div key={i} className="faq-option-card">
                <h5 className="faq-option-title">{opt.title}</h5>
                <p className="faq-option-subtitle">{opt.subtitle}</p>
                
                <div className="faq-info-group">
                  <span className="faq-group-label">Required Information:</span>
                  <ul className="faq-bullet-list">
                    {opt.required.map((req, ri) => (
                      <li key={ri} className="faq-bullet-item">
                        <span className="faq-bullet-dot">•</span> {req}
                      </li>
                    ))}
                  </ul>
                </div>

                {opt.optional && opt.optional.length > 0 && (
                  <div className="faq-info-group mt-2">
                    <span className="faq-group-label">Optional:</span>
                    <ul className="faq-bullet-list optional">
                      {opt.optional.map((optItem, oi) => (
                        <li key={oi} className="faq-bullet-item">
                          <span className="faq-bullet-dot">•</span> {optItem}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
          {item.notice && (
            <div className="faq-notice-alert">
              <span className="faq-asterisk">*</span> {item.notice}
            </div>
          )}
        </div>
      );
    }

    return <div className="faq-plain-text">{item.answer}</div>;
  };

  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <button className="faq-header" onClick={onClick}>
        <span className="faq-question">{item.question}</span>
        <div className="faq-icon-wrap">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              d="M6 1V11M1 6H11"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </button>

      {/* Height transition via max-height */}
      <div className="faq-answer-wrap">
        <div className="faq-answer">{renderAnswerContent()}</div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   FAQ SECTION
───────────────────────────────────────── */
export default function FAQSection() {
  const { faqs, loading } = useSite();
  const [activeTab, setActiveTab] = useState('personal');
  const [openIndex, setOpenIndex] = useState(0);

  const currentFaqs = faqs?.[activeTab] || [];

  const switchTab = (tab) => {
    setActiveTab(tab);
    setOpenIndex(0);
  };

  return (
    <section id="faq" className="faq-section">
      <div className="faq-container">

        {/* Header */}
        <div className="faq-head">
          <Fade bottom>
            <span className="faq-eyebrow">FAQ</span>
            <h2 className="faq-heading">
              Frequently asked{' '}
              <span className="faq-accent">questions</span>
            </h2>
            <p className="faq-subline">
              Everything you need to know about the product and banking.
            </p>
          </Fade>

          {/* Tabs */}
          <Fade bottom delay={80}>
            <div className="faq-tabs">
              {['personal', 'business'].map((tab) => (
                <button
                  key={tab}
                  className={`faq-tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => switchTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </Fade>
        </div>

        {/* List */}
        <div className="faq-list">
          {loading
            ? [1, 2, 3].map((i) => (
                <div key={i} style={{ marginBottom: '12px' }}>
                  <Skeleton height={60} borderRadius={14} />
                </div>
              ))
            : currentFaqs.map((item, index) => (
                <Fade bottom delay={index * 50} key={`${activeTab}-${index}`}>
                  <FAQItem
                    item={item}
                    isOpen={openIndex === index}
                    onClick={() =>
                      setOpenIndex(openIndex === index ? -1 : index)
                    }
                  />
                </Fade>
              ))}
        </div>
      </div>

      {/* ── Styles ── */}
      <style>{`
        /* Section */
        .faq-section {
          padding: 100px 24px;
          background: #f9fafb;
        }

        .faq-container {
          max-width: 760px;
          margin: 0 auto;
        }

        /* Head */
        .faq-head {
          text-align: center;
          margin-bottom: 52px;
        }

        .faq-eyebrow {
          display: inline-block;
          color: #ff4400;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          margin-bottom: 14px;
        }

        .faq-heading {
          font-size: clamp(1.9rem, 3.5vw, 2.6rem);
          font-weight: 900;
          color: #0f172a;
          line-height: 1.1;
          letter-spacing: -0.04em;
          margin: 0 0 14px;
        }

        .faq-accent {
          color: #ff4400;
        }

        .faq-subline {
          font-size: 15.5px;
          color: #64748b;
          line-height: 1.65;
          margin: 0 auto 28px;
          max-width: 480px;
        }

        /* Tabs */
        .faq-tabs {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 999px;
        }

        .faq-tab {
          padding: 8px 22px;
          border-radius: 999px;
          border: none;
          background: transparent;
          font-size: 13px;
          font-weight: 700;
          color: #64748b;
          cursor: pointer;
          transition: background 0.22s ease, color 0.22s ease;
          letter-spacing: 0.02em;
        }

        .faq-tab.active {
          background: #ff4400 !important;
          color: #fff !important;
          box-shadow: 0 4px 14px rgba(255, 103, 0, 0.28);
        }

        /* List */
        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        /* Item */
        .faq-item {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          overflow: hidden;
          transition: border-color 0.25s ease, box-shadow 0.25s ease;
        }

        .faq-item.open {
          border-color: #ff4400;
          box-shadow: 0 4px 24px rgba(255, 103, 0, 0.08);
        }

        /* Header button */
        .faq-header {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 20px 22px;
          background: transparent;
          border: none;
          cursor: pointer;
          text-align: left;
        }

        .faq-question {
          font-size: 15px;
          font-weight: 700;
          color: #0f172a;
          line-height: 1.4;
          flex: 1;
          transition: color 0.2s ease;
        }

        .faq-item.open .faq-question {
          color: #ff4400;
        }

        /* Icon — rotates 45deg on open (plus → cross) */
        .faq-icon-wrap {
          flex-shrink: 0;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          transition:
            transform 0.3s ease,
            background 0.25s ease,
            color 0.25s ease;
        }

        .faq-item.open .faq-icon-wrap {
          transform: rotate(45deg);
          background: #ff4400;
          color: #fff;
        }

        /* Answer — max-height accordion */
        .faq-answer-wrap {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.38s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .faq-item.open .faq-answer-wrap {
          max-height: 1600px; /* generous cap for structured lists */
        }

        .faq-answer {
          padding: 0 22px 20px;
          font-size: 14.5px;
          color: #64748b;
          line-height: 1.75;
          border-top: 1px solid #f1f5f9;
          padding-top: 16px;
        }

        /* Structured FAQ Styling */
        .faq-structured-answer {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .faq-intro {
          font-size: 14px;
          font-weight: 600;
          color: #334155;
          margin-bottom: 8px;
        }

        .faq-options-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
        }

        .faq-option-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          transition: all 0.25s ease;
        }

        .faq-option-card:hover {
          border-color: rgba(255, 68, 0, 0.2);
          background: #fff;
          box-shadow: 0 8px 24px rgba(255, 68, 0, 0.04);
        }

        .faq-option-title {
          font-size: 15px;
          font-weight: 800;
          color: #0f172a;
          margin: 0;
          line-height: 1.3;
        }

        .faq-option-subtitle {
          font-size: 12px;
          color: #64748b;
          line-height: 1.5;
          margin: 0;
        }

        .faq-info-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .faq-group-label {
          font-size: 10px;
          font-weight: 700;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .faq-bullet-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: grid;
          grid-template-columns: 1fr;
          gap: 4px;
        }

        .faq-option-card:nth-of-type(2) .faq-bullet-list {
          grid-template-columns: 1fr 1fr;
          gap: 6px;
        }

        .faq-bullet-item {
          font-size: 13px;
          color: #475569;
          display: flex;
          align-items: center;
          gap: 6px;
          line-height: 1.4;
        }

        .faq-bullet-dot {
          color: #ff4400;
          font-weight: bold;
        }

        .faq-notice-alert {
          background: rgba(255, 68, 0, 0.04);
          border: 1px dashed rgba(255, 68, 0, 0.24);
          border-radius: 10px;
          padding: 12px 16px;
          font-size: 12.5px;
          color: #475569;
          line-height: 1.5;
          margin-top: 8px;
        }

        .faq-asterisk {
          color: #ff4400;
          font-weight: bold;
        }

        @media (max-width: 768px) {
          .faq-options-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
          .faq-option-card:nth-of-type(2) .faq-bullet-list {
            grid-template-columns: 1fr;
          }
        }

        /* Responsive */
        @media (max-width: 600px) {
          .faq-section {
            padding: 72px 16px;
          }

          .faq-heading {
            font-size: 1.75rem;
          }

          .faq-header {
            padding: 16px 16px;
          }

          .faq-answer {
            padding: 12px 16px 16px;
          }
        }
      `}</style>
    </section>
  );
}
